/* Everything a marketer would want to change without touching layout code.

   The plan limits mirror what the backend enforces (backend/saas.py, PLANS):
   keep the two in step. Channel lists come from CHANNELS below, so the
   landing page, the footer and the FAQ can never disagree (PRD section 17). */

export const PRODUCT = "AutoCruise";

// The company that owns and develops the product - shown in the footer and
// named as the provider in the terms and privacy policy.
export const COMPANY = "Optimml";
export const COMPANY_URL = "https://optimml.com";

export const CONTACT_EMAIL = "hello@autocruise.online";   // placeholder - set a real inbox

// Shown in the final call to action while access opens in waves. Set to ""
// once sign-up is fully open.
export const WAVE_STATUS = "Early access · opening in waves";

/* One channel list for the whole site (PRD 17: "one channel list from the
   product config"). Only what ships. */
export const CHANNELS = {
  publishes: ["WordPress", "LinkedIn", "Instagram", "Facebook", "Threads"],
  approves: ["The app", "Telegram", "WhatsApp"],
  listens: ["RSS & Atom feeds", "Any article URL", "Your content pillars"],
};

/* The plan table. `limits` feeds both the cards and the comparison table. */
export const LIMIT_ROWS = [
  ["posts", "Posts / month"],
  ["images", "Images / month"],
  ["brands", "Brand systems"],
  ["feeds", "News feeds"],
  ["accounts", "Connected accounts"],
  ["wp", "WordPress sites"],
  ["seats", "Seats"],
  ["approvals", "Approvals"],
  ["engine", "Autonomous engine"],
];

export const PLANS = [
  {
    key: "free",
    name: "Free",
    price: "$0",
    period: "forever",
    blurb: "See what it makes for your brand.",
    limits: { posts: "20", images: "40", brands: "1", feeds: "1", accounts: "3", wp: "—", seats: "1",
              approvals: "Manual", engine: "—" },
    cta: "Start free",
    to: "/signup",
  },
  {
    key: "pro",
    name: "Pro",
    price: "$29",
    period: "per month",
    blurb: "For a brand that posts every day.",
    limits: { posts: "250", images: "600", brands: "3", feeds: "10", accounts: "15", wp: "2", seats: "3",
              approvals: "All modes", engine: "Included" },
    // Card billing is not live yet, so Pro is set up on request rather than
    // promising a self-serve start the visitor cannot actually make.
    cta: "Request Pro",
    to: `mailto:${CONTACT_EMAIL}?subject=AutoCruise%20Pro`,
    featured: true,
  },
  {
    key: "agency",
    name: "Agency",
    price: "$99",
    period: "per month",
    blurb: "Many clients, one command centre.",
    limits: { posts: "1,500", images: "4,000", brands: "25", feeds: "50", accounts: "100", wp: "25", seats: "15",
              approvals: "All + client", engine: "Included" },
    cta: "Talk to us",
    to: `mailto:${CONTACT_EMAIL}?subject=AutoCruise%20Agency`,
  },
];

const list = (items) => items.slice(0, -1).join(", ") + " and " + items[items.length - 1];

export const FAQ = [
  {
    q: "Does it really post without me?",
    a: "Only if you let it. Every workspace chooses: manual (every post waits for a tap), auto (posts that pass the hard checks publish on schedule), or quality (those checks plus an editorial review scored against your brand). New workspaces start on manual. Anything that fails is held for you with the reason written on it: never silently dropped, never silently published.",
  },
  {
    q: "What counts as a post?",
    a: "One piece of content for one brand, however many channels it goes to. A carousel is one post. A WordPress article is one post, and the social post that promotes it is another. Images are counted separately, one per rendered image or slide.",
  },
  {
    q: "What if the gate is wrong?",
    a: "Then you overrule it. A held post shows exactly which check failed and why, and you can edit, approve or reject it from the app, Telegram or WhatsApp. Approving over a failed check is recorded in the audit log with your name. The gate never publishes something it failed; the worst it does is ask you.",
  },
  {
    q: "Where does it get its ideas?",
    a: "From your brand's content pillars, from the headlines your news feeds have just fetched, and from what you have already posted, which it deliberately avoids repeating. News posts are written from the actual article, not from the model's imagination. Topics you block for a brand are never written about.",
  },
  {
    q: "Which platforms does it publish to?",
    a: `${list(CHANNELS.publishes)}. LinkedIn carousels go out as real document carousels, and WordPress articles carry their featured image and SEO fields. Approvals can come from the app, Telegram or WhatsApp.`,
  },
  {
    q: "Will it look like my brand?",
    a: "Each brand has its own system: palette, type, logo placement, image rules, voice and banned words. Every image and caption is built against it. Upload your brand guidelines as a PDF and it fills the system in for you to check.",
  },
  {
    q: "Can it send traffic to my website?",
    a: "Yes. Connect a WordPress site and a feed can publish the full article first, then post about it with the link, on a ratio you set, because platforms rank link posts lower. Links are tagged with UTM parameters so your own analytics see where visits came from.",
  },
  {
    q: "Is my data separate from other customers'?",
    a: "Every workspace is isolated at the database layer: each query is scoped to your workspace before it runs, and anything that tries to read without one is refused outright. Your content is never used to train shared models.",
  },
];

/* The film's chapters, at the times its acts begin (promo/scene.html). */
export const FILM = {
  src: "/promo/autocruise-promo.mp4",
  poster: "/promo/autocruise-poster.jpg",
  runtime: "52 sec",
  chapters: [["0:00", "The calendar problem"], ["0:06", "It listens"], ["0:14", "It creates"],
             ["0:24", "It checks"], ["0:32", "It ships"], ["0:42", "You stay in charge"]],
};
